import { c as _c } from "react/compiler-runtime";
import { useMutation, type UseMutationResult, useQuery, type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import type * as fetch from 'shared/api/fetch';
import * as analytics from 'shared/api/analytics';
import { type AdPreviewResp, getAccountAdPreview, type Platforms } from 'shared/api/accounts';
import { createDashboard, type CreateDashboardReq, type DashboardResp, deleteDashboard, getDashboard, getDashboards, updateDashboard } from 'shared/api/dashboards';
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics';
import { isNilOrEmpty } from 'shared/utils';
export const queryKeys = {
  dashboards: 'dashboards',
  accountAdPreview: 'accountAdPreview'
} as const;
export const useQueryDashboards = (clientId, t0) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId) {
    t1 = [queryKeys.dashboards, clientId];
    t2 = async () => await getDashboards(clientId);
    $[0] = clientId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = Boolean(clientId) && enabled;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
};
export const useQueryDashboardsByRefId = (clientId, refId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== refId) {
    t1 = [queryKeys.dashboards, clientId, refId];
    t2 = async () => await getDashboards(clientId, refId);
    $[0] = clientId;
    $[1] = refId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && enabled && refId > 0;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useQueryDashboard = (dashboardId, clientId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== dashboardId) {
    t1 = [queryKeys.dashboards, clientId, dashboardId];
    t2 = async () => await getDashboard(dashboardId, clientId);
    $[0] = clientId;
    $[1] = dashboardId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && enabled;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useMutationDeleteDashboard = () => {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [queryKeys.dashboards];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== queryClient) {
    t1 = {
      mutationKey: t0,
      mutationFn: _temp,
      onMutate: async deletedDashboard => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.dashboards, deletedDashboard.clientId]
        });
        const existingDashboards = queryClient.getQueryData([queryKeys.dashboards, deletedDashboard.clientId]);
        queryClient.setQueryData([queryKeys.dashboards, deletedDashboard.clientId], oldDashboards => oldDashboards != null ? oldDashboards.filter(dashboard => dashboard.id !== deletedDashboard.dashboardId) : []);
        return {
          existingDashboards,
          clientId: deletedDashboard.clientId
        };
      },
      onError: (_, __, context) => {
        if (context?.existingDashboards !== undefined && context?.clientId !== undefined) {
          queryClient.setQueryData([queryKeys.dashboards, context.clientId], context.existingDashboards);
        }
      },
      onSettled: (__0, ___0, variables) => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.dashboards, variables.clientId]
        });
      }
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useMutationCreateDashboard = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.dashboards, data.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationUpdateDashboard = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onMutate: async updatedDashboard => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.dashboards, updatedDashboard.clientId, updatedDashboard.dashboardId]
        });
        await queryClient.cancelQueries({
          queryKey: [queryKeys.dashboards, updatedDashboard.clientId]
        });
        const existingDashboard = queryClient.getQueryData([queryKeys.dashboards, updatedDashboard.clientId, updatedDashboard.dashboardId]);
        const existingDashboards = queryClient.getQueryData([queryKeys.dashboards, updatedDashboard.clientId]);
        queryClient.setQueryData([queryKeys.dashboards, updatedDashboard.clientId, updatedDashboard.dashboardId], oldDashboard => ({
          ...oldDashboard,
          ...updatedDashboard.dashboard
        }));
        queryClient.setQueryData([queryKeys.dashboards, updatedDashboard.clientId], oldDashboards => oldDashboards?.map(dashboard_0 => dashboard_0.id === updatedDashboard.dashboardId ? {
          ...dashboard_0,
          ...updatedDashboard.dashboard
        } : dashboard_0) ?? []);
        return {
          existingDashboard,
          existingDashboards
        };
      },
      onError: (_, __, context) => {
        if (context?.existingDashboard != null) {
          queryClient.setQueryData([queryKeys.dashboards, context.existingDashboard.clientId, context.existingDashboard.id], context.existingDashboard);
        }
        if (context?.existingDashboards !== undefined && context?.existingDashboard !== undefined) {
          queryClient.setQueryData([queryKeys.dashboards, context.existingDashboard.clientId], context.existingDashboards);
        }
      },
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.dashboards, data.clientId, data.id]
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.dashboards, data.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationCreativePerformanceSummary = () => {
  return useMutationAnalytics(_temp4);
};
export const useMutationCreativePerformanceTimeSeries = () => {
  return useMutationAnalytics(_temp5);
};
export const useMutationCreativeMap = () => {
  return useMutationAnalytics(_temp6);
};

// Conversion events types
export interface ConversionEventResp {
  conversionName: string;
  conversionSource: string;
  conversions: number | null;
}
export interface ConversionEventsReq {
  clientId: number;
  id: string;
  report: analytics.ReportType;
  filters?: analytics.Filter[];
}
export const useMutationConversionEvents = () => {
  return useMutationAnalytics(_temp7);
};
export type FilterDistinctValuesResp = Record<string, string | number>;
export interface BatchFilterDistinctValuesReq {
  clientId: number;
  id: string;
  fieldNames: string[];
  filters?: analytics.Filter[];
}
export interface BatchFilterDistinctValuesResp {
  fieldName: string;
  fieldValue: string | number;
}
export const useMutationBatchFilterDistinctValues = () => {
  return useMutationAnalytics(_temp9);
};
export const useQueryAccountAdPreview = t0 => {
  const $ = _c(16);
  const {
    clientId,
    accountId,
    creativeId,
    platform,
    adFormat
  } = t0;
  let t1;
  let t2;
  if ($[0] !== accountId || $[1] !== adFormat || $[2] !== clientId || $[3] !== creativeId || $[4] !== platform) {
    t1 = [queryKeys.accountAdPreview, clientId, accountId, creativeId, platform, adFormat];
    t2 = async () => await getAccountAdPreview(clientId, accountId as number, creativeId as string, platform as Platforms, adFormat);
    $[0] = accountId;
    $[1] = adFormat;
    $[2] = clientId;
    $[3] = creativeId;
    $[4] = platform;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[7] !== accountId || $[8] !== clientId || $[9] !== creativeId || $[10] !== platform) {
    t3 = !isNilOrEmpty(clientId) && !isNilOrEmpty(accountId) && !isNilOrEmpty(creativeId) && !isNilOrEmpty(platform);
    $[7] = accountId;
    $[8] = clientId;
    $[9] = creativeId;
    $[10] = platform;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      throwOnError: false,
      enabled: t3
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return useQuery(t4);
};
async function _temp(t0) {
  const {
    dashboardId,
    clientId
  } = t0;
  await deleteDashboard(dashboardId, clientId);
}
async function _temp2(t0) {
  const {
    dashboard
  } = t0;
  return await createDashboard(dashboard);
}
async function _temp3(t0) {
  const {
    dashboard,
    clientId,
    dashboardId
  } = t0;
  return await updateDashboard(dashboard, clientId, dashboardId);
}
async function _temp4(data) {
  return await analytics.postCreativePerformanceReport(data);
}
async function _temp5(data) {
  return await analytics.postCreativePerformanceReport(data);
}
async function _temp6(data) {
  return await analytics.postPlatformCreativeReport(data);
}
async function _temp7(variables) {
  return await analytics.postReport({
    collection: "standard_reports",
    ...variables
  });
}
function _temp8(name) {
  return {
    name
  };
}
async function _temp9(variables) {
  const {
    fieldNames,
    ...rest
  } = variables;
  return await analytics.postReport({
    collection: "standard_reports",
    report: "creative_insights_distinct_values_report",
    fields: fieldNames.map(_temp8),
    ...rest
  });
}